<template>
  <div class="setup-fee-pending-card-wrapper">
    <div
      v-if="checkoutData && checkoutData.status === 'pending'"
      class="setup-fee-pending-card"
      data-testid="setup-fee-pending-card"
    >
      <div class="card-content">
        <div class="card-icon">
          <i class="pi pi-exclamation-circle" />
        </div>
        <div class="card-info">
          <h4 class="card-title" data-testid="setup-fee-pending-title">
            {{ t('Taxa de implantação pendente') }}
          </h4>
          <p class="card-description" data-testid="setup-fee-pending-description">
            {{ franchiseName ? `${franchiseName}: ` : '' }}
            {{ formattedAmount }}
            <!-- Number concatenated OUTSIDE t(): a {n} placeholder inside an
                 API-driven key renders literally until the key is translated
                 (the grace_display lesson on MyFranchisesPage). -->
            <span v-if="checkoutData.installments > 1">
              ({{ t('em até') }} {{ checkoutData.installments }}x)
            </span>
          </p>
        </div>
        <div class="card-actions">
          <app-button
            :label="t('Pagar taxa de implantação')"
            icon="pi pi-credit-card"
            severity="warn"
            size="small"
            :loading="checkingOutFranquiaId === franquiaId"
            :disabled="checkingOutFranquiaId === franquiaId"
            data-testid="setup-fee-pending-pay-btn"
            @click="handlePay"
          />
        </div>
      </div>
      <div v-if="checkoutError" class="card-error" data-testid="setup-fee-pending-error">
        {{ checkoutError }}
      </div>
      <comprovante-do-setup :franquia-id="franquiaId" class="card-comprovante" />
    </div>
  </div>
</template>


<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSetupFeeCheckout } from '../../composables/useSetupFeeCheckout'
import { currencyFactor } from '../../utils/currencyMinorUnits'
import AppButton from '../../ui/AppButton.vue'
import ComprovanteDoSetup from '../implantacao/ComprovanteDoSetup.vue'
import type { SetupFee_CheckoutResponseDados } from '../../contracts/SetupFee_CheckoutResponse'

const { t, n } = useI18n()
const { checkingOutFranquiaId, checkoutError, fetchCheckout, checkoutAndRedirect } = useSetupFeeCheckout()

const props = defineProps<{
  franquiaId: number
  franchiseName?: string
}>()

const checkoutData = ref<SetupFee_CheckoutResponseDados | null>(null)

const formattedAmount = computed(() => {
  if (!checkoutData.value) return ''
  const currency = checkoutData.value.currency || 'BRL'
  const major = checkoutData.value.amount_cents / currencyFactor('pt-BR', currency)
  return n(major, { style: 'currency', currency })
})

async function loadCheckout(): Promise<void> {
  if (!props.franquiaId) return
  try {
    checkoutData.value = await fetchCheckout(props.franquiaId)
  } catch {
    checkoutData.value = null
  }
}




async function handlePay(): Promise<void> {
  if (!props.franquiaId) return
  try {
    await checkoutAndRedirect(props.franquiaId)
  } catch {
    // Error is already captured in checkoutError ref
  }
}

watch(() => props.franquiaId, () => {
  loadCheckout()
})

onMounted(() => {
  loadCheckout()
})
</script>

<style scoped>
.setup-fee-pending-card {
  background-color: var(--p-amber-50, #fffbeb);
  border: 1px solid var(--p-amber-300, #fcd34d);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}
.card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.card-icon {
  font-size: 1.5rem;
  color: var(--p-amber-600, #d97706);
  display: flex;
  align-items: center;
}
.card-info {
  flex: 1;
}
.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-amber-900, #78350f);
  margin: 0 0 0.25rem 0;
}
.card-description {
  font-size: 0.875rem;
  color: var(--p-amber-800, #92400e);
  margin: 0;
}
.card-actions {
  display: flex;
  align-items: center;
}
.card-comprovante {
  margin-top: 12px;
}

.card-error {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--p-red-600, #dc2626);
}
</style>
